<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
        Competitor Analysis Results
      </h3>
      <button
        @click="$emit('proceed-to-quality')"
        class="inline-flex items-center px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium">
        Review Quality Profile
      </button>
    </div>

    <div
      v-if="benchmarkConfidence?.warnings?.length"
      class="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <span class="text-xs font-medium text-amber-800 dark:text-amber-300">
          Benchmark confidence: {{ benchmarkConfidence.confidence_label }} ({{ benchmarkConfidence.confidence_score }}%)
        </span>
        <span class="text-xs text-amber-700 dark:text-amber-400">
          {{ benchmarkConfidence.analyzed_count }}/{{ benchmarkConfidence.selected_count }} analyzed
        </span>
      </div>
      <p v-for="warning in benchmarkConfidence.warnings" :key="warning" class="text-xs text-amber-800 dark:text-amber-300">
        {{ warning }}
      </p>
    </div>

    <div v-if="failedCompetitors.length" class="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
      <h4 class="text-xs font-medium text-red-800 dark:text-red-300 mb-2">
        Failed Competitor Extractions
      </h4>
      <div v-for="comp in failedCompetitors" :key="comp.id" class="text-xs text-red-700 dark:text-red-300">
        {{ comp.domain || comp.url }}: {{ comp.error_message || 'Extraction failed' }}
      </div>
    </div>

    <!-- Dimension tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700 mb-4">
      <nav class="flex gap-4 -mb-px overflow-x-auto">
        <button
          v-for="dim in dimensions"
          :key="dim.key"
          @click="activeDimension = dim.key"
          :class="[
            'pb-2 px-1 text-xs font-medium border-b-2 whitespace-nowrap',
            activeDimension === dim.key
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          ]">
          {{ dim.label }}
        </button>
      </nav>
    </div>

    <!-- Headings -->
    <div v-if="activeDimension === 'headings'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
          <div class="flex flex-wrap items-center justify-end gap-2">
            <span class="text-xs text-gray-500 dark:text-gray-400">SEO relevance</span>
            <span :class="gradeBadge(comp.heading_grade)" class="px-2 py-0.5 rounded-lg text-xs font-bold">{{ comp.heading_grade }}</span>
            <template v-if="headingStructure(comp)">
              <span class="text-xs text-gray-500 dark:text-gray-400">Structure</span>
              <span :class="gradeBadge(headingStructure(comp).grade)" class="px-2 py-0.5 rounded-lg text-xs font-bold">{{ headingStructure(comp).grade }}</span>
            </template>
          </div>
        </div>
        <!-- Relevance components -->
        <div v-if="headingSemantic(comp)?.components" class="mb-3 flex flex-wrap gap-2 text-xs">
          <span class="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            Relevance score {{ headingSemantic(comp).score }}
          </span>
          <span class="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            Target keyword in {{ headingSemantic(comp).target_match_pct }}% of headings
          </span>
          <span class="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            Location: {{ locationSignalLabel(headingSemantic(comp).location_signal) }}
          </span>
          <span class="px-2 py-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            {{ headingSemantic(comp).components.descriptive_pct }}% descriptive · {{ headingSemantic(comp).components.generic_pct }}% generic
          </span>
          <span v-if="headingSemantic(comp).components.duplicate_pct > 0" class="px-2 py-0.5 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
            {{ headingSemantic(comp).components.duplicate_pct }}% duplicates
          </span>
        </div>
        <!-- Anti-patterns -->
        <div v-if="comp.anti_patterns && comp.anti_patterns.length" class="mb-2">
          <span class="text-[11px] text-gray-500 dark:text-gray-400 mr-2">Structure warnings:</span>
          <span v-for="(ap, i) in comp.anti_patterns" :key="i" class="inline-block mr-2 mb-1 px-2 py-0.5 rounded-lg text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
            {{ ap.message }}
          </span>
        </div>
        <!-- Heading tree -->
        <div v-if="comp.heading_analysis && comp.heading_analysis.classified_headings" class="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 space-y-1">
          <div
            v-for="(h, i) in comp.heading_analysis.classified_headings"
            :key="i"
            :style="{ paddingLeft: `${(h.level - 1) * 16}px` }"
            class="text-sm flex items-center gap-2">
            <span class="text-xs text-gray-400 w-6">H{{ h.level }}</span>
            <span :class="classificationColor(h.classification)">
              {{ classificationIcon(h.classification) }}
            </span>
            <span class="text-gray-700 dark:text-gray-300" :class="{ 'font-medium': h.has_keyword }" :title="h.reason">
              {{ h.text }}
            </span>
            <span v-if="h.has_location" class="text-[10px] px-1 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">city</span>
            <span v-if="h.is_question" class="text-xs text-blue-500">Q</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Titles & Meta -->
    <div v-else-if="activeDimension === 'titles'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
        </div>
        <div v-if="comp.title_meta_analysis" class="space-y-3">
          <div>
            <dt class="text-xs text-gray-500 uppercase">
              Title Tag
            </dt>
            <dd class="text-sm mt-1">
              {{ comp.title_meta_analysis.title?.meta_title || 'N/A' }}
            </dd>
            <div class="flex gap-2 mt-1">
              <span class="text-xs text-gray-400">{{ comp.title_meta_analysis.title?.length || 0 }} chars</span>
              <span v-if="comp.title_meta_analysis.title?.rewritten_by_google" class="text-xs text-amber-600">Google rewrote</span>
              <span :class="gradeBadge(comp.title_meta_analysis.title?.grade)" class="px-1.5 py-0.5 rounded text-xs font-bold">{{ comp.title_meta_analysis.title?.grade }}</span>
            </div>
          </div>
          <div>
            <dt class="text-xs text-gray-500 uppercase">
              Meta Description
            </dt>
            <dd class="text-sm mt-1">
              {{ comp.title_meta_analysis.meta?.text || 'N/A' }}
            </dd>
            <div class="flex gap-2 mt-1">
              <span class="text-xs text-gray-400">{{ comp.title_meta_analysis.meta?.length || 0 }} chars</span>
              <span :class="gradeBadge(comp.title_meta_analysis.meta?.grade)" class="px-1.5 py-0.5 rounded text-xs font-bold">{{ comp.title_meta_analysis.meta?.grade }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Images -->
    <div v-else-if="activeDimension === 'images'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
          <span class="text-xs text-gray-500">{{ comp.image_analysis?.summary?.total || 0 }} images, {{ comp.image_analysis?.summary?.alt_percentage || 0 }}% with alt</span>
        </div>
      </div>
    </div>

    <!-- Links -->
    <div v-else-if="activeDimension === 'links'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
        </div>
        <div v-if="comp.link_analysis?.summary" class="flex flex-wrap gap-3 text-xs">
          <template v-if="comp.link_analysis.regions_available">
            <span class="font-medium text-gray-900 dark:text-gray-100">{{ comp.link_analysis.contextual_internal_count || 0 }} contextual internal</span>
            <span class="text-gray-500">{{ comp.link_analysis.template_internal_count || 0 }} nav/footer/template</span>
            <span class="text-gray-400">({{ comp.link_analysis.internal_link_count || 0 }} internal total)</span>
          </template>
          <span v-else class="text-gray-500">{{ comp.link_analysis.internal_link_count || 0 }} internal (regions not captured; re-extract to split nav/footer)</span>
          <span class="text-gray-500">{{ comp.link_analysis.summary.total_external || 0 }} external</span>
          <span class="text-green-600">{{ comp.link_analysis.summary.authoritative || 0 }} authoritative</span>
          <span v-if="comp.link_analysis.summary.spam > 0" class="text-red-500">{{ comp.link_analysis.summary.spam }} spam</span>
        </div>
      </div>
    </div>

    <!-- Readability -->
    <div v-else-if="activeDimension === 'readability'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
        </div>
        <div v-if="comp.readability_metrics && !comp.readability_metrics.error" class="grid grid-cols-3 gap-3">
          <div v-for="(val, key) in comp.readability_metrics" :key="key" class="text-center">
            <div class="text-lg font-bold text-gray-900 dark:text-gray-100">
              {{ val }}
            </div>
            <div class="text-xs text-gray-500">
              {{ formatMetricLabel(key) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- E-E-A-T -->
    <div v-else-if="activeDimension === 'eeat'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
            <span v-if="comp.eeat_signals?.profile" class="px-2 py-0.5 rounded-lg text-[11px] bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
              {{ profileLabel(comp.eeat_signals.profile) }}
            </span>
          </div>
          <span v-if="comp.eeat_signals?.overall_eeat_score != null" class="text-sm font-bold">{{ comp.eeat_signals.overall_eeat_score }}/100</span>
        </div>
        <div v-if="comp.eeat_signals?.signals" class="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div v-for="(signal, key) in visibleSignals(comp.eeat_signals.signals)" :key="key" class="flex items-center gap-2 text-xs">
            <span :class="signal.present ? 'text-green-600' : 'text-gray-400'">{{ signal.present ? '✓' : '✗' }}</span>
            <span class="text-gray-700 dark:text-gray-300" :title="signal.evidence">{{ signal.label || key.replace(/_/g, ' ') }}</span>
            <span v-if="signal.applicability" :class="applicabilityBadge(signal.applicability)" class="px-1.5 py-0.5 rounded-lg text-[10px]">
              {{ applicabilityLabel(signal.applicability) }}
            </span>
          </div>
        </div>
        <button
          v-if="hiddenSignalCount(comp.eeat_signals?.signals) > 0 || showNaSignals"
          type="button"
          class="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline"
          @click="showNaSignals = !showNaSignals">
          {{ showNaSignals ? 'Hide not-applicable signals' : `Show ${hiddenSignalCount(comp.eeat_signals?.signals)} not-applicable signals` }}
        </button>
        <p v-if="comp.eeat_signals?.source === 'heuristic'" class="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
          Detected by text patterns (LLM review unavailable).
        </p>
        <p v-if="comp.eeat_signals?.summary" class="text-xs text-gray-500 mt-2">
          {{ comp.eeat_signals.summary }}
        </p>
      </div>
    </div>

    <!-- Freshness -->
    <div v-else-if="activeDimension === 'freshness'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
        </div>
        <div v-if="comp.freshness_data && !comp.freshness_data.error" class="text-sm space-y-1">
          <div v-if="comp.freshness_data.publish_date">
            <span class="text-gray-500">Published:</span> {{ comp.freshness_data.publish_date }}
          </div>
          <div v-if="comp.freshness_data.modified_date">
            <span class="text-gray-500">Updated:</span> {{ comp.freshness_data.modified_date }}
          </div>
          <div v-if="comp.freshness_data.age_days">
            <span class="text-gray-500">Age:</span> {{ comp.freshness_data.age_days }} days
          </div>
        </div>
      </div>
    </div>

    <!-- Above-Fold -->
    <div v-else-if="activeDimension === 'above_fold'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
          <span v-if="comp.above_fold_structure?.profile" class="px-2 py-0.5 rounded-lg text-[11px] bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
            {{ profileLabel(comp.above_fold_structure.profile) }}
          </span>
        </div>
        <div v-if="comp.above_fold_structure && !comp.above_fold_structure.error" class="flex flex-wrap gap-2">
          <span
            v-for="(val, key) in aboveFoldFlags(comp.above_fold_structure)"
            :key="key"
            :class="val ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'"
            class="px-2 py-0.5 rounded-lg text-xs">
            {{ val ? '✓' : '✗' }} {{ key }}
          </span>
        </div>
      </div>
    </div>

    <!-- Schema -->
    <div v-else-if="activeDimension === 'schema'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
          <span class="text-xs text-gray-500">{{ comp.schema_analysis?.total_schemas || 0 }} schemas</span>
        </div>
        <div v-if="comp.schema_analysis" class="flex flex-wrap gap-2">
          <span v-for="schema in comp.schema_analysis.json_ld_schemas || []" :key="schema.type" class="px-2 py-0.5 rounded text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
            {{ schema.type }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  competitors: { type: Array, required: true },
  serpAnalysis: { type: Object, required: true },
  benchmarkConfidence: { type: Object, default: null }
})

defineEmits(['proceed-to-quality'])

const activeDimension = ref('headings')
const showNaSignals = ref(false)

const dimensions = [
  { key: 'headings', label: 'Headings (relevance + structure)' },
  { key: 'titles', label: 'Titles & Meta' },
  { key: 'images', label: 'Images' },
  { key: 'links', label: 'Links' },
  { key: 'readability', label: 'Readability' },
  { key: 'eeat', label: 'E-E-A-T' },
  { key: 'freshness', label: 'Freshness' },
  { key: 'above_fold', label: 'Above-Fold' },
  { key: 'schema', label: 'Schema' }
]

const analyzedCompetitors = computed(() => props.competitors.filter((c) => c.overall_quality_grade))
const failedCompetitors = computed(() => props.competitors.filter((c) => c.extraction_status === 'failed'))

const gradeBadge = (grade) => {
  const map = {
    A: 'bg-green-100 dark:bg-green-900/30 text-green-700',
    B: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700',
    C: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700',
    D: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700',
    F: 'bg-red-100 dark:bg-red-900/30 text-red-700'
  }
  return map[grade] || map.F
}

const classificationColor = (cls) => ({
  seo: 'text-green-600',
  descriptive: 'text-blue-500',
  functional: 'text-amber-500',
  formatting: 'text-red-500'
})[cls] || 'text-gray-400'

const classificationIcon = (cls) => ({
  seo: '✓',
  descriptive: '•',
  functional: '⚠',
  formatting: '✗'
})[cls] || '?'

const headingStructure = (comp) => comp.heading_analysis?.structure || null
const headingSemantic = (comp) => comp.heading_analysis?.semantic || null

const locationSignalLabel = (signal) => ({
  h1_and_subheadings: 'in H1 and subheadings',
  h1: 'in H1 only',
  subheadings: 'subheadings only',
  none: 'missing',
  not_applicable: 'n/a for this keyword'
})[signal] || 'unknown'

const profileLabel = (profile) => ({
  local_service: 'Local service profile',
  article: 'Article profile'
})[profile] || profile

const applicabilityLabel = (value) => ({
  required: 'Required',
  helpful: 'Helpful',
  not_applicable: 'N/A'
})[value] || value

const applicabilityBadge = (value) => ({
  required: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
  helpful: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  not_applicable: 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
})[value] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'

const visibleSignals = (signals) => {
  if (showNaSignals.value) return signals
  return Object.fromEntries(Object.entries(signals || {}).filter(([, signal]) => signal.applicability !== 'not_applicable'))
}

const hiddenSignalCount = (signals) => Object.values(signals || {}).filter((signal) => signal.applicability === 'not_applicable').length

const formatMetricLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const aboveFoldFlags = (structure) => {
  if (structure.profile === 'local_service') {
    return {
      H1: structure.has_h1,
      'H1 has service + location': structure.h1_has_service_and_location,
      'Value proposition': structure.has_value_proposition,
      'Phone / tel link': structure.has_phone_or_tel_link,
      CTA: structure.has_cta,
      'Business identity': structure.has_business_identity,
      'Trust signal': structure.has_trust_signal,
      'Hero visual': structure.has_hero_visual,
      'Reviews snippet': structure.has_reviews_snippet
    }
  }

  return {
    H1: structure.has_h1,
    'Hero Image': structure.has_hero_image,
    Breadcrumbs: structure.has_breadcrumbs,
    TOC: structure.has_table_of_contents,
    Author: structure.has_author_info,
    Date: structure.has_date,
    CTA: structure.has_cta_above_fold
  }
}
</script>
